import React, { useEffect, useState, useRef, useCallback } from 'react';
import * as Y from 'yjs';
import { Excalidraw, exportToCanvas } from '@excalidraw/excalidraw';
import { ExcalidrawBinding } from 'y-excalidraw';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createNamespacedProvider } from '../../yjsProvider';
import { useDispatch } from 'react-redux';
import { setZoomedMediaId } from '../../../MediaPlayback/store/livekitSlice/livekitSlice';
import MediaContainer from '../../../MediaPlayback/components/MediaDisplay/MediaContainer/MediaContainer';
import { jsonToUint8Array } from '../../../../utils/jsonSerializer';
import { ParticipantDataManager } from '../../../../utils/participantDataManager';
import './Whiteboard.css';

import { mapSocketFactory } from '../../../../utils/socketFactory.js'


/**
 * 
 * @param {LocalParticipant} localParticipant 
 * @param {Map<string, RemoteParticipant>} remoteParticipants 
 * @returns 
 */
export default function Whiteboard({ localParticipant, remoteParticipants }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { localAvatar, token } = useSelector((s) => s.auth);
  const { conferenceMode } = useSelector(state => state.livekit);
  const { whiteboardActive, whiteboardId } = useSelector((state) => state.whiteboard);

  const [api, setApi] = useState(null);
  const [yjsProvider, setYjsProvider] = useState(null);
  const [excalidrawBinding, setExcalidrawBinding] = useState(null);
  const [participantCount, setParticipantCount] = useState(1);

  const participantDataManager = useRef(new ParticipantDataManager())


  useEffect(() => {
    if (!api) return;

    const handleResize = () => {
      api.refresh();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [api]);



  /* ---------- Unzoom handler ---------- */
  const unzoom = useCallback(() => {
    dispatch(setZoomedMediaId(null));
  }, [dispatch]);
  /* ---------- Yjs provider ---------- */
  useEffect(() => {
    // If whiteboard is inactive, clean up and exit
    if (!whiteboardActive) {
      if (yjsProvider) {
        console.log("[Whiteboard] Whiteboard deactivated – destroying provider");
        yjsProvider.disconnect();
        yjsProvider.destroy();
        setYjsProvider(null);
      }
      return;
    }

    // If active but no ID, nothing to do
    if (!whiteboardId) {
      return;
    }

    // If we already have a provider for this exact whiteboard, keep it
    if (yjsProvider && yjsProvider.whiteboardId === whiteboardId) {
      return;
    }

    // Clean up previous provider if switching boards
    if (yjsProvider) {
      console.log("[Whiteboard] Switching whiteboards – destroying old provider");
      yjsProvider.disconnect();
      yjsProvider.destroy();
    }

    console.log("[Whiteboard] Creating new provider for ID:", whiteboardId);

    let provider = null;
    let awarenessCleanup = null;

    try {
      provider = createNamespacedProvider(whiteboardId, {
        name: localAvatar?.display_name,
        email: localAvatar?.email,
        id: localAvatar?.id,
        token: token,
      });

      if (!provider) {
        console.error("[Whiteboard] Failed to create provider");
        return;
      }

      provider.whiteboardId = whiteboardId;

      const updateParticipants = () => {
        const count = provider.getParticipantCount();
        setParticipantCount(count);
        console.log("[Whiteboard] Participants:", count);
      };

      provider.onAwarenessReady((awareness) => {
        awareness.on('change', updateParticipants);
        awarenessCleanup = () => awareness.off('change', updateParticipants);
        updateParticipants();
      });

      console.log("[Whiteboard] Connecting provider...");
      provider.connect();

      setYjsProvider(provider);
      dispatch(setZoomedMediaId(whiteboardId));
      console.log("[Whiteboard] Provider initialized and connected");

      // Cleanup only if this provider is still the current one
      return () => {
        if (provider && (!yjsProvider || provider.whiteboardId === yjsProvider.whiteboardId)) {
          console.log("[Whiteboard] Cleanup: destroying provider");
          if (awarenessCleanup) {
            awarenessCleanup();
          }
          provider.disconnect();
          provider.destroy();
          if (yjsProvider === provider) {
            setYjsProvider(null);
          }
        }
      };
    } catch (error) {
      console.error("[Whiteboard] Error creating whiteboard provider:", error);
    }
  }, [whiteboardActive, whiteboardId, localAvatar?.id, localAvatar?.email, localAvatar?.display_name]);

  /* ---------- Excalidraw binding ---------- */
  useEffect(() => {
    if (!api || !yjsProvider) {
      console.log("[Whiteboard] Binding: waiting for API or provider", { api: !!api, provider: !!yjsProvider });
      return;
    }

    console.log("[Whiteboard] Setting up Excalidraw binding");

    let binding = null;
    let dead = false;

    const tryBind = () => {
      if (dead) {
        console.log("[Whiteboard] Binding: aborted (dead)");
        return;
      }


      console.log("[Whiteboard] Server ready, creating binding");

      /* 1. capture the state we want (DB snapshot) */
      const snapshot = Y.encodeStateAsUpdate(yjsProvider.doc);
      console.log("[Whiteboard] Captured snapshot:", snapshot.length, "bytes");

      /* 2. destroy the local update log */
      Y.transact(yjsProvider.doc, () => {
        Y.applyUpdate(yjsProvider.doc, new Uint8Array([0, 0])); // empty update
      });

      /* 3. re-apply the snapshot (now with zero history) */
      Y.applyUpdate(yjsProvider.doc, snapshot);

      /* 4. now create the binding → no stale update inside */
      yjsProvider.onAwarenessReady((awareness) => {
        binding = new ExcalidrawBinding(
          yjsProvider.doc.getArray('elements'),
          yjsProvider.doc.getMap('assets'),
          api,
          awareness
        );

        setExcalidrawBinding(binding);
        console.log("[Whiteboard] Excalidraw binding created");
      });

    };

    tryBind();

    return () => {
      console.log("[Whiteboard] Cleaning up binding");
      dead = true;
      if (binding) {
        binding.destroy();
        setExcalidrawBinding(null);
      }
    };
  }, [api, yjsProvider]);


  /* ---------- Pointer update emitter ---------- */

  const handlePointerUpdate = useCallback((payload) => {
    if (!excalidrawBinding || !yjsProvider || !localParticipant?.publishData || !whiteboardId) {
      return;
    }
    // Transform the payload to a unit8array
    const u8array = jsonToUint8Array({
      pointer: payload.pointer,
      button: payload.button,
      topic: `whiteboard/${whiteboardId}/pointers`
    });
    localParticipant.publishData(u8array, {
      reliable: false,
    });
  }, [excalidrawBinding, yjsProvider, localParticipant, whiteboardId]);



  /* ---------- Pointer update handler ---------- */

  const handlePointerData = useCallback((participantId, payload, meta) => {
    console.log("api", api, !api)
    if (!api) return;


    try {
      // Get existing collaborators and merge with the new pointer data
      const currentCollaborators = api.getAppState().collaborators;
      const updatedCollaborators = new Map(currentCollaborators);

      updatedCollaborators.set(participantId, {
        pointer: {
          x: payload.pointer.x,
          y: payload.pointer.y,
          tool: payload.pointer.tool || "pointer"
        },
        button: payload.button || "up",
        selectedElementIds: payload.selectedElementIds || {},
        username: meta.name || "Anonymous",
      });

      // Update the scene with merged collaborators
      api.updateScene({
        collaborators: updatedCollaborators
      });
    } catch (error) {
      console.error("[Whiteboard] Failed to handle pointer data:", error);
    }
  }, [api]);

  const handlePointerDataRef = useRef(handlePointerData);
  useEffect(() => {
    handlePointerDataRef.current = handlePointerData;
  }, [handlePointerData]);



  /* ---------- Pointer update listener ---------- */
  useEffect(() => {
    if (!yjsProvider || !participantDataManager.current) {
      return;
    }
    let cleanup = null;

    // Subscribe to pointer events on the specific whiteboard topic
    try {
      const topic = `whiteboard/${whiteboardId}/pointers`;
      // Proxy to avoid stale closure
      const listenerProxy = (...args) => {
        if (handlePointerDataRef.current) {
          handlePointerDataRef.current(...args);
        }
      };
      const cleanup = participantDataManager.current.onTopic(
        topic,
        listenerProxy
      );

      return () => {
        if (cleanup) cleanup();
      };
    } catch (error) {
      console.error("[Whiteboard] Error setting up pointer listener:", error);
      if (cleanup) cleanup();
    }
  }, [yjsProvider, whiteboardId]);

  /* ---------- Update participants when they change ---------- */
  useEffect(() => {
    if (!participantDataManager.current) {
      return;
    }
    try {
      remoteParticipants.keys()
    } catch {
      console.error("Remote participants is not a map", typeof (remoteParticipants), remoteParticipants);
      return
    }

    // Update manager whenever participants change
    participantDataManager.current.onParticipantsUpdated(remoteParticipants ? remoteParticipants : new Map());
  }, [remoteParticipants]);




  /* ---------- Export PNG ---------- */
  const exportPNG = useCallback(async () => {
    if (!api) {
      console.warn("[Whiteboard] Cannot export: no API");
      return;
    }
    console.log("[Whiteboard] Exporting PNG");
    try {
      const canvas = await exportToCanvas({
        elements: api.getSceneElements(),
        appState: api.getAppState(),
        exportPadding: 10,
        mimeType: 'image/png',
        backgroundColor: '#fff',
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = 'whiteboard.png';
      link.click();
    } catch (error) {
      console.error("[Whiteboard] Export error:", error);
    }
  }, [api]);


  const currentRoom = useSelector((state) => state.MapDisplay.currentRoom);

  const closeWhiteboard = useCallback(() => {
    const newState = !whiteboardActive;

    // On émet vers le serveur
    mapSocketFactory.socket.emit("createWhiteboard", {
      roomId: currentRoom.room_id,
      boardId: 'whiteboard-' + localAvatar?.id,
      active: newState,
      owner: localAvatar?.email
    });
  }, [localAvatar?.id, currentRoom, whiteboardActive])

  /* ---------- Render ---------- */
  return (
    <MediaContainer
      mediaId={whiteboardId}
      canZoom
      canFullscreen={false}
      canFloat={false}
      containerType="video"
      className="camera-off"
    >
      {/*  Badge layer – only visible when tile is NOT zoomed  */}
      <div className="wb-badge-layer">
        <div className='header-wb-badge-layer'>
          <span className="wb-badge-label">Tableau blanc</span>
          <span className='tag-wb-badge-layer'>
            <span class="material-symbols-outlined">
              image
            </span>
            Exporter
          </span>
        </div>
        <div className='body-wb-badge-layer'>
          <span class="wb-fullscreen material-symbols-outlined">
            pan_zoom
          </span>
        </div>

      </div>

      {/*  Zoomed layer – only visible when tile IS zoomed  */}
      <div className={`wb-zoomed-layer
      ${conferenceMode ? "wb-zoomed-layer-conference-mode" : ""}`}
        onClick={(e) => e.stopPropagation()}>
        <header className="wb-header" onPointerDown={(e) => e.stopPropagation()}>
          <h2 className="wb-title">{t('whiteboard_tooltip')} <small><span className="material-symbols-outlined">groups</span> {participantCount}</small></h2>
          {/* <div className="wb-participants">
              <span className="material-symbols-outlined">groups</span>
              <span>
                {participantCount} {t('participants')}
              </span>
            </div> */}
          <div className="wb-buttons-container">
            <button className="wb-export" onClick={exportPNG}>
              <span className="material-symbols-outlined">image</span>
              <span className='wb-label-btn'>{t('Exporter')}</span>
            </button>
            {whiteboardId === 'whiteboard-' + localAvatar?.id &&
              <button className="wb-close" onClick={closeWhiteboard}>
                <span class="material-symbols-outlined">
                  close
                </span>
              </button>
            }
          </div>
        </header>

        <div className="wb-canvas-box">
          <Excalidraw
            excalidrawAPI={setApi}
            theme="light"
            isCollaborating={true}
            onPointerUpdate={handlePointerUpdate}
            UIOptions={{
              canvasActions: {
                changeViewBackgroundColor: false,
                clearCanvas: false,
                loadScene: false,
                export: false,
              },
            }}
          />
        </div>
        <button className="wb-reduce" onClick={unzoom}>
          <span className="material-symbols-outlined">close_fullscreen</span>
        </button>
      </div>
    </MediaContainer>
  );
}